import { commonText, useCommonLanguage } from "../i18n/commonPresentation";
import { useMemo } from "react";

import { getAppStatusFromCache } from "./useSystemApps";

export type AppStatusKey = "healthy" | "degraded" | "stopped" | "unknown" | "update";

export type AppStatusMeta = {
  status: AppStatusKey;
  installedVersion: string | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  /** Whether the upstream package is available for download. Defaults to true. */
  packageAvailable: boolean;
  updatedAt: string | null;
  message?: string;
  managePath?: string;
};

const FALLBACK_STATUS: AppStatusMeta = {
  status: "unknown",
  installedVersion: null,
  latestVersion: null,
  updateAvailable: false,
  packageAvailable: true,
  updatedAt: null,
  get message() { return commonText("statusUnavailable"); },
};

export const useAppStatus = (appId: string) => {
  useCommonLanguage();
  const data = useMemo<AppStatusMeta>(() => {
    const cached = getAppStatusFromCache(appId);
    if (!cached) return FALLBACK_STATUS;

    // Derive a user-facing message from the status data.
    // Stopped is always primary, but include update info when available.
    let message: (() => string) | undefined;
    if (!cached.active) {
      message = cached.updateAvailable && cached.latestVersion
        ? () => commonText("serviceStoppedUpdate", { version: cached.latestVersion! })
        : () => commonText("serviceStopped");
    } else if (cached.updateAvailable && cached.latestVersion) {
      message = () => commonText("versionAvailable", { version: cached.latestVersion! });
    }

    // Map backend status to UI status key.
    // Use `active` boolean directly (ground truth from systemd) rather than derived `status`.
    // Priority: stopped (red) > update (amber, only when running) > healthy (green)
    let status: AppStatusKey;
    if (!cached.active) {
      // Service is not running - always show stopped (red)
      status = "stopped";
    } else if (cached.updateAvailable) {
      // Running with update available - show update (amber)
      status = "update";
    } else {
      // Running, no updates - show healthy (green)
      status = "healthy";
    }

    return {
      status,
      installedVersion: cached.installedVersion,
      latestVersion: cached.latestVersion,
      updateAvailable: cached.updateAvailable,
      packageAvailable: cached.packageAvailable !== false,
      updatedAt: null, // Not available from current API
      get message() { return message?.(); },
    };
  }, [appId]);

  return { data, loading: false, error: undefined as string | undefined };
};
